import { ENGAGEMENT_PATH, ENGAGEMENT_TAB_PARAM } from '@/features/engagement/engagement-tabs';
import { LIMITS_PATH, LIMITS_TAB_PARAM } from '@/features/limits/limits-tabs';
import { TEAM_PATH, TEAM_TAB_PARAM } from '@/features/team/team-tabs';
import {
  STUDIO_TARGET_PARAM,
  WIDGET_PATH,
} from '@/features/widget-design-studio/studio-targets.types';

import type { EngagementTab } from '@/features/engagement/engagement-tabs';
import type { LimitsTab } from '@/features/limits/limits-tabs';
import type { TeamTab } from '@/features/team/team-tabs';
import type { StudioTargetId } from '@/features/widget-design-studio/studio-targets.types';

/**
 * How each PANED route addresses its panes, and what each pane is called.
 *
 * **The query-param name is a fact about the PAGE, not about the setting.** All
 * sixteen `/settings/workspace` anchors are revealed by the same `?tab=`; all
 * ten Design Studio panes by the same `?target=`. Writing the param on each
 * anchor would be thirty-one copies of one page-level fact, every one of them
 * independently mis-typeable and none of them checkable against the `path`
 * sitting one argument away.
 *
 * It does not ADD a table. `settings-search-index.ts` already carried this one,
 * as `TAB_LABEL_KEY` — keyed `path → pane → i18n key`. The param name is the
 * column it was missing, and absorbing it here is what lets `settingAnchorHref`
 * stop writing `?tab=` as a string literal.
 */

export interface SettingAnchorRoute {
  /** The query param that selects a pane on this route. */
  readonly param: string;
  /**
   * Every pane the route accepts → the i18n key naming it in a breadcrumb, or
   * `null` where THE PANE IS THE UNIT.
   *
   * `null` is a decision rather than a gap. `/connections`, `/team` and
   * `/widget` index one anchor PER pane, so the anchor's own label already is
   * the pane's name ("Welcome screen") and a trail segment would say the same
   * word twice. `/settings/workspace`, `/engagement` and `/limits` group several
   * cards under one pane, so the pane's name is real information.
   *
   * One column rather than two: these keys are also the accepted-value list, so
   * there is no second list of pane ids to drift out of step.
   */
  readonly panes: Readonly<Record<string, string | null>>;
}

/** The workspace-settings route, named once because a dozen anchors point at it. */
export const SETTINGS = '/settings/workspace';

/** `/connections` has no exported tab vocabulary — see the residual note below. */
const CONNECTIONS_PATH = '/connections';

/**
 * Annotated with each page's OWN exported vocabulary wherever one exists, so a
 * new tab is a compile error here until somebody decides whether it is
 * addressable — and `PaneOf` inherits the closed union for free.
 */
const ENGAGEMENT_PANES: Readonly<Record<EngagementTab, string>> = {
  suggestions: 'engagement.tab.suggestions',
  nudges: 'engagement.tab.nudges',
  placement: 'engagement.tab.placement',
  rules: 'engagement.tab.rules',
};

const LIMITS_PANES: Readonly<Record<LimitsTab, string>> = {
  budget: 'limits.tab.budget',
  alerts: 'limits.tab.alerts',
  traffic: 'limits.tab.traffic',
};

const TEAM_PANES: Readonly<Record<TeamTab, null>> = {
  members: null,
  roles: null,
  invitations: null,
};

const STUDIO_PANES: Readonly<Record<StudioTargetId, null>> = {
  launcher: null,
  panel: null,
  header: null,
  welcome: null,
  messages: null,
  composer: null,
  disclosure: null,
  components: null,
  brand: null,
  language: null,
};

export const SETTING_ANCHOR_ROUTES = {
  [SETTINGS]: {
    param: 'tab',
    panes: {
      general: 'workspacesettings.tab.general',
      data: 'workspacesettings.tab.data',
      backups: 'workspacesettings.tab.backups',
      danger: 'workspacesettings.tab.danger',
    },
  },
  [ENGAGEMENT_PATH]: { param: ENGAGEMENT_TAB_PARAM, panes: ENGAGEMENT_PANES },
  [LIMITS_PATH]: { param: LIMITS_TAB_PARAM, panes: LIMITS_PANES },
  [TEAM_PATH]: { param: TEAM_TAB_PARAM, panes: TEAM_PANES },
  [CONNECTIONS_PATH]: {
    param: 'tab',
    panes: {
      mcp: null,
      federation: null,
      byok: null,
      'byo-endpoint': null,
      origins: null,
      webhooks: null,
      whatsapp: null,
      domain: null,
    },
  },
  [WIDGET_PATH]: { param: STUDIO_TARGET_PARAM, panes: STUDIO_PANES },
} as const satisfies Record<string, SettingAnchorRoute>;

/** A route that addresses panes. */
export type PanedPath = keyof typeof SETTING_ANCHOR_ROUTES;

/** The panes one route accepts — the keys of its own column. */
export type PaneOf<P extends PanedPath> = keyof (typeof SETTING_ANCHOR_ROUTES)[P]['panes'] & string;

/**
 * A widened view for the two runtime lookups. The `satisfies` const above stays
 * literal so `PaneOf` can read it; indexing it by an arbitrary string cannot.
 */
const ROUTES: Readonly<Record<string, SettingAnchorRoute>> = SETTING_ANCHOR_ROUTES;

/** The query param that reveals a pane on this route. */
export function paneParamFor(path: string): string | undefined {
  return ROUTES[path]?.param;
}

/** The i18n key naming a pane, or `undefined` where the pane needs no trail segment. */
export function paneLabelKey(path: string, pane: string | undefined): string | undefined {
  if (pane === undefined) return undefined;
  return ROUTES[path]?.panes[pane] ?? undefined;
}

/**
 * Two routes still write `param: 'tab'` by hand — `/settings/workspace`, whose
 * `TABS` tuple is private inside its page component, and `/connections`, whose
 * tab type is a bare `string`. Exporting those vocabularies the way
 * `engagement-tabs.ts`, `limits-tabs.ts` and `team-tabs.ts` already do would
 * close `PaneOf` for them too. Named rather than done here: it is a change to
 * two pages that has nothing to do with the Design Studio.
 */
